/**
 * Schemas describing ReploAgent session shape: the session "type" enum that
 * routes prompts on the coordinator, plus the per-session metadata that the
 * agent-ui keeps in its store so it can render type-specific UI (titles,
 * completion rows, etc.) without re-deriving from RPC args.
 *
 * @author Kevin OConnell 2026-04-21
 */
import * as z from "zod";

import { uiMessagePartsSchema } from "./mentionable";
import { selectModeSourceLocationSchema } from "./selectMode";
import { siteReportTypeSchema } from "./siteReport";

// The ordered pipeline stages of a publish, shared by the harness, coordinator, and debugger; lives here (not agentConnection) because the publish session metadata embeds it and agentConnection already imports this module.
export const publishPhaseSchema = z
  .enum(["build", "upload", "publisher", "tag"])
  .describe("PublishPhase");

export const opencodeSessionTypeSchema = z
  .enum([
    "main",
    "subagent",
    "selectModeInlineEdit",
    "publish",
    "gitConflict",
    "duplicatePage",
    "deletePage",
    "analyticsScriptDetection",
    "filesystemSession",
    "scheduledTask",
    "memoryCuration",
    "tutorialPersonalization",
    "starterSuggestions",
    "proactiveFindings",
    "siteReport",
  ])
  .describe("OpencodeSessionType");

export const chatSessionTypeSchema = opencodeSessionTypeSchema
  .extract(["main", "scheduledTask"])
  .describe("ChatSessionType");

export const opencodeSessionSourceSchema = z
  .enum([
    "agent-ui",
    "miniapp",
    "task",
    "slack",
    "public-api",
    "public-mcp",
    "linq",
    "admin",
  ])
  .describe("OpencodeSessionSource");

// Chat recall scope: miniapp sessions are embedded widget chats, not conversations
// the user thinks of as "my chats", and an admin session is a Replo operator's work
// on the project rather than a conversation the user had.
export const chatSessionSourceSchema = opencodeSessionSourceSchema
  .exclude(["miniapp", "admin"])
  .describe("ChatSessionSource");

const opencodeMiniappSourceMetadataSchema = z.object({
  id: z.string(),
  reuseIdentifier: z.string().optional(),
});

const opencodeAgentUiSourceMetadataSchema = z.object({
  reuseIdentifier: z.string().optional(),
});

const opencodeSlackSourceMetadataSchema = z.object({
  slackWorkspaceId: z.string(),
  slackChannelId: z.string(),
  slackThreadId: z.string(),
});

// Linq (Messages) is invisible iMessage/SMS transport: a turn is keyed by the
// chat thread it belongs to. The sender's phone number stays in the responder
// and is never persisted with the session.
const opencodeLinqSourceMetadataSchema = z.object({
  chatId: z.string(),
});

const opencodeTaskSourceMetadataSchema = z.object({
  taskRunId: z.string(),
});

const opencodeAgentUiSourceContextSchema = z.object({
  source: z.literal("agent-ui"),
  sourceMetadata: opencodeAgentUiSourceMetadataSchema,
});

const opencodeSlackSourceContextSchema = z.object({
  source: z.literal("slack"),
  sourceMetadata: opencodeSlackSourceMetadataSchema,
});

const opencodeLinqSourceContextSchema = z.object({
  source: z.literal("linq"),
  sourceMetadata: opencodeLinqSourceMetadataSchema,
});

export const connectedChatTargetSchema = z
  .discriminatedUnion("source", [
    opencodeSlackSourceContextSchema,
    opencodeLinqSourceContextSchema,
  ])
  .describe("ConnectedChatTarget");

const opencodeMiniappSourceContextSchema = z.object({
  source: z.literal("miniapp"),
  sourceMetadata: opencodeMiniappSourceMetadataSchema,
});

const opencodeTaskSourceContextSchema = z.object({
  source: z.literal("task"),
  sourceMetadata: opencodeTaskSourceMetadataSchema,
});

// Shared by the public REST API and the public MCP server: both create sessions
// through bedrock's public request path, differing only in the source label.
export const publicMcpSessionBindingSchema = z
  .object({
    toolName: z.string().min(1),
    initialRequestHash: z.string().optional(),
    target: z.discriminatedUnion("resource", [
      z.object({
        resource: z.literal("insightsReport"),
        projectId: z.uuid(),
        fileId: z.uuid().nullable(),
      }),
      z.object({
        resource: z.literal("artifact"),
        projectId: z.uuid(),
        fileId: z.uuid().nullable(),
      }),
      z.object({ resource: z.literal("brandKit"), projectId: z.uuid() }),
      z.object({ resource: z.literal("site"), siteId: z.uuid() }),
      z.object({
        resource: z.literal("sitePage"),
        siteId: z.uuid(),
        route: z.string().min(1),
      }),
    ]),
  })
  .describe("PublicMcpSessionBinding");

const opencodePublicRequestSourceMetadataSchema = z.object({
  requestId: z.string(),
  mcpSessionBinding: publicMcpSessionBindingSchema.optional(),
});

const opencodePublicApiSourceContextSchema = z.object({
  source: z.literal("public-api"),
  sourceMetadata: opencodePublicRequestSourceMetadataSchema,
});

const opencodePublicMcpSourceContextSchema = z.object({
  source: z.literal("public-mcp"),
  sourceMetadata: opencodePublicRequestSourceMetadataSchema,
});

// A Replo employee acting from Replo Admin on a customer's project. Kept apart from the public sources so
// operator-initiated turns are distinguishable from the customer's own in session history and analytics.
const opencodeAdminSourceMetadataSchema = z.object({
  /** The superuser who pressed the button, not the project's own user the session runs as. */
  actorUserId: z.string(),
  tool: z.literal("v1-migration"),
});

const opencodeAdminSourceContextSchema = z.object({
  source: z.literal("admin"),
  sourceMetadata: opencodeAdminSourceMetadataSchema,
});

export const opencodeSourceContextSchema = z
  .discriminatedUnion("source", [
    opencodeAgentUiSourceContextSchema,
    opencodeSlackSourceContextSchema,
    opencodeLinqSourceContextSchema,
    opencodeMiniappSourceContextSchema,
    opencodeTaskSourceContextSchema,
    opencodePublicApiSourceContextSchema,
    opencodePublicMcpSourceContextSchema,
    opencodeAdminSourceContextSchema,
  ])
  .describe("OpencodeSourceContext");

// NOTE (Kevin O'Connell, 2026-04-22, REPL-26014): Per-element snapshot projected
// into `source-element` chips; geometry-only fields are excluded to save bytes.
export const selectModeEditElementSnapshotSchema = z
  .object({
    kind: z.literal("react-element"),
    tagName: z.string(),
    // Tag as authored in JSX source when it differs from the rendered DOM
    // `tagName` (e.g. `Image` for a Next.js `<Image>` that renders `<img>`).
    // Absent for elements authored directly as their DOM tag.
    sourceTagName: z.string().optional(),
    elementType: z.string(),
    textContent: z.string().nullable(),
    sectionLabel: z.string().optional(),
    sourceLocation: selectModeSourceLocationSchema.optional(),
  })
  .describe("SelectModeEditElementSnapshot");

export const selectModeEditPlanSnapshotSchema = z
  .object({
    kind: z.literal("plan"),
    planId: z.string(),
    label: z.string(),
  })
  .describe("SelectModeEditPlanSnapshot");

export const selectModeEditSelectableSnapshotSchema = z
  .discriminatedUnion("kind", [
    selectModeEditElementSnapshotSchema,
    selectModeEditPlanSnapshotSchema,
  ])
  .describe("SelectModeEditSelectableSnapshot");

// NOTE (Kevin O'Connell, 2026-04-23, REPL-26014): Variants mirror
// `selectModeSelectionTypeSchema` in ./selectMode so the metadata
// vocabulary stays in lockstep with the runtime one. The `drag` variant
// is structurally representable but unreachable in normal flow —
// `SelectModeInput` hides the submit panel while a drag is in progress.
// We carry it anyway so `projectSelectModeSelectionToMetadata` can stay
// a total `exhaustiveSwitch` over `SelectModeSelection`.
export const selectModeEditSelectionMetadataSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("none") }),
    z.object({
      type: z.literal("element"),
      data: selectModeEditSelectableSnapshotSchema,
    }),
    z.object({
      type: z.literal("area"),
      items: z.array(selectModeEditSelectableSnapshotSchema),
    }),
    z.object({ type: z.literal("drag") }),
  ])
  .describe("SelectModeEditSelectionMetadata");

// NOTE (Kevin, 2026-04-21): Single source of truth for side-session
// variants. `sideSessionMetadataSchema` exposes these directly;
// `sessionMetadataSchema` adds the non-side-session branches (`main`,
// `subagent`) on top. Adding a new side session means editing this tuple
// only — both unions stay in sync automatically.
const sideSessionMetadataOptions = [
  z.object({
    type: z.literal("selectModeInlineEdit"),
    pageRoute: z.string(),
    currentSandboxFilePath: z.string().nullable(),
    // Ties the session back to the canopy optimistic override applied at commit
    // time so it can be cleared once this edit finishes streaming. Optional so
    // sessions started before this field existed still validate.
    editId: z.string().optional(),
  }),
  z.object({ type: z.literal("deletePage"), route: z.string() }),
  z.object({
    type: z.literal("duplicatePage"),
    route: z.string(),
    // The site the copy lands in, so a project's other sites don't list it while it's being made. Optional so
    // sessions started before this field existed still validate.
    siteId: z.uuid().optional(),
  }),
  z.object({
    type: z.literal("publish"),
    // The site this publish targets, set by the client when the publish starts.
    // Read back off the coordinator's metadata broadcast to label the popover
    // ("Publishing <name>") and survive a reload.
    siteId: z.uuid(),
    // Client-written. Absent means every page with unpublished changes.
    promoteRoutes: z.array(z.string()).optional(),
    // Coordinator-written, write-once. "succeeded" the moment a publish tool
    // part completes (the live site is updated then); "failed" if the session
    // concludes without one. Absent while the publish is in flight.
    outcome: z.enum(["succeeded", "failed"]).optional(),
    // (Cole, 2026-07-09, REPL-27972): coordinator-written display state — the in-flight publish's current pipeline phase; overwritten as phases advance and never read for control flow.
    phase: publishPhaseSchema.optional(),
    content: z
      .object({
        route: z.string().startsWith("/"),
        operation: z.enum(["publish", "unpublish", "archive"]),
      })
      .optional(),
  }),
  z.object({ type: z.literal("gitConflict") }),
  z.object({ type: z.literal("memoryCuration") }),
  z.object({ type: z.literal("tutorialPersonalization") }),
  z.object({ type: z.literal("starterSuggestions") }),
  z.object({ type: z.literal("proactiveFindings") }),
  z.object({
    type: z.literal("analyticsScriptDetection"),
    providerKey: z.string(),
    configValue: z.string(),
    action: z.enum(["add", "remove"]),
  }),
] as const;

export const sideSessionMetadataSchema = z
  .discriminatedUnion("type", sideSessionMetadataOptions)
  .describe("SideSessionMetadata");

// What the coordinator hands the publish tool for a `publish` session: the
// route selection the trigger (editor, public API) already decided, written
// to that session's own file in the sandbox. `promoteRoutes` follows the
// session metadata: absent means every page and entry with changes, [] means
// site-wide changes only.
export const publishSessionSelectionSchema = z
  .object({
    promoteRoutes: z.array(z.string()).optional(),
  })
  .describe("PublishSessionSelection");

// The prompt every `publish` session is started with, whether the trigger is
// the editor Publish button or the public `publish_site` tool. The coordinator
// auto-approves the publish permission for this session type, so the prompt
// must not leave the agent room to pause on the "which site" question.
export function buildPublishPrompt({
  siteId,
  promoteRoutes,
}: {
  siteId: string;
  promoteRoutes?: string[];
}): string {
  const selection = promoteRoutes
    ? `Call the publish tool with scope "routes" and routes set to exactly ${JSON.stringify(promoteRoutes)} — only those routes' drafts (pages and content entries) go live; every other draft stays unpublished. `
    : `Call the publish tool with scope "all" so every page and content entry with changes goes live. `;
  return [
    `Publish the site with siteId ${siteId} now.`,
    `${selection}Publish that site — do not ask which site, and do not go looking for its folder on disk; the publish tool finds the site's folder itself.`,
    "Follow the publishing-sites skill exactly as written.",
    "Do NOT ask the user any questions about the publishing process — this was triggered by the user explicitly requesting a publish, so proceed without confirmation and do not pause to ask whether to commit or whether to retry.",
    "First run the publish tool.",
    "If publish fails due to build/runtime issues, use Website debugger to query runtime errors and logs, fix actionable issues, and retry publish.",
  ].join(" ");
}

// The prompt a `Refine with agent` press from the v1 migration admin tool starts its session with. The
// deterministic compiler has already written the route; this hands the page to the agent to close the gap
// the compiler cannot — dynamic data, interactions, and every `data-v1-todo` it left behind. The element id
// and route are named because a fresh session has neither, and the skill needs both to rebuild its bundle.
// `v1-migration-check` is the only judge of when the page is done, so the prompt makes it the turn's exit.
export function buildV1MigrationRefinePrompt({
  elementId,
  route,
  siteId,
  siteDirectory,
  liveUrl,
}: {
  elementId: string;
  route: string;
  siteId: string;
  siteDirectory: string;
  liveUrl: string | null;
}): string {
  const extractFlags = `--extract --element ${elementId} --route ${route} --site ${siteDirectory}`;
  const extractCommand = liveUrl
    ? `\`${extractFlags}\` \`--live-url ${liveUrl}\``
    : `\`${extractFlags}\``;
  const paragraphs = [
    [
      `Use the migrating-v1-pages skill to finish migrating v1 element ${elementId} into route ${route} of the site with siteId ${siteId}, whose working tree is the directory ${siteDirectory}.`,
      "Read the skill first.",
      ...(liveUrl ? [`The live v1 page is ${liveUrl}.`] : []),
      `The route was already compiled by the same script and committed, but the bundle it compiled from lived in /tmp and is gone, so start by running the skill's step 1 as written: ${extractCommand}.`,
      "That rebuilds the bundle, the reference crops and COMPILE-REPORT.md, and recompiles the route to the same output that is already committed, so nothing is lost.",
      "Then read the brief.",
    ],
    [
      "Order of work: (1) wire the commerce markers first — option and variant selects, quantity selectors, add-to-cart and every other cart action — against the product loader and cart hooks, before any pixel or layout polish, because the page looks finished at rest while those controls do nothing; (2) resolve every other data-v1-todo marker; (3) work the checker loop exactly as the brief describes.",
      'A section you leave `off` needs a measured cause in its report notes — the scorer band, the layout diff items and the attempts the stuck rule asks for; "font rounding" or "sub-pixel differences" is not a cause.',
      'A section only a person can fix (an integration to connect, products missing from the store) records what they must do in its `needsUser` instead, and your last message ends with a "What you need to do" list of them.',
    ],
    [
      "Whenever you believe the page is done, call the `v1-migration-check` tool and do exactly what it returns: it counts the markers left, then asks for the judging round, which you run yourself, and tells you what is still owed.",
      "Your turn ends only when `v1-migration-check` says the migration is done or blocked.",
      "Never end a turn with a progress update or a note about what you will do next; keep working instead.",
      "If you are genuinely blocked (the site cannot express something, or the data does not exist), record the blocker the way `v1-migration-check` tells you to and call it again; stop once it says blocked.",
    ],
    [
      "Do not ask which site or which element; both are named above.",
      "Do not publish.",
    ],
  ];
  return paragraphs.map((sentences) => sentences.join(" ")).join("\n\n");
}

export type V1MigrationHandoffPage = {
  elementId: string;
  name: string;
  kind: "page" | "productTemplate" | "article" | "section";
  route: string;
  liveUrl: string | null;
  /** The product routes a template renders, which the agent checks against. */
  productRoutes: string[];
  /** Where the live theme shows a section, which the agent's pages must match. */
  sectionPlacements?: {
    file: string;
    position: number;
    of: number;
    path: string | null;
  }[];
  todos: { kind: string; detail?: string }[];
};

const HANDOFF_KIND_LABELS: Record<V1MigrationHandoffPage["kind"], string> = {
  page: "Page",
  productTemplate: "Product template",
  article: "Blog post",
  section: "Theme section",
};

// A template's or section's own folder is private to Next, so its checkers load a page that renders it.
function handoffPreviewPath(page: V1MigrationHandoffPage) {
  if (page.kind === "productTemplate") {
    return page.productRoutes[0] ?? null;
  }
  if (page.kind === "section") {
    return (
      page.sectionPlacements?.find((placement) => placement.path)?.path ?? null
    );
  }
  return null;
}

/**
 * What the site judge scores each page from. A theme section no page of the live theme shows has nowhere to
 * be scored, so it is left out.
 */
export function handoffJudgedPages(
  pages: V1MigrationHandoffPage[],
): z.infer<typeof v1MigrationRefinePageSchema>[] {
  return pages.flatMap((page) => {
    const previewPath =
      page.kind === "page" || page.kind === "article"
        ? page.route
        : handoffPreviewPath(page);
    if (!previewPath) {
      return [];
    }
    return [
      {
        route: page.route,
        elementId: page.elementId,
        liveUrl: page.liveUrl,
        previewPath,
      },
    ];
  });
}

function handoffKindNotes(
  page: V1MigrationHandoffPage,
  previewPath: string | null,
) {
  if (page.kind === "productTemplate") {
    return [
      `   It renders through template.tsx (not page.tsx) for ${page.productRoutes.length} product route(s) under /products/. The product comes from the route's handle; the bundle command points the checkers at ${previewPath ?? "one of those routes"}.`,
    ];
  }
  if (page.kind === "article") {
    return [
      "   The v1 content is the post's body. The live page wraps it in the theme's article layout (title, featured image, date, author); build that around it from the live page.",
    ];
  }
  if (page.kind === "section") {
    const placements = (page.sectionPlacements ?? []).map((placement) => {
      return `${placement.file} (section ${placement.position} of ${placement.of}${placement.path ? `, shown at ${placement.path}` : ""})`;
    });
    return [
      `   A section the merchant placed in their theme, compiled as a component (export default V1Section) in app${page.route}/section.tsx, not a page. The live theme shows it in: ${placements.join("; ")}. Render it in the site's matching pages or layout at the same position among their sections, building whatever of the surrounding page the site lacks. The bundle command points the checkers at ${previewPath ?? "a page showing it"}.`,
    ];
  }
  return [];
}

// The coordinator runs compile-site.mjs over a hand-off's routes before its first turn (packages/v1-compiler
// src/compile-site.ts), so what several pages repeat is one component by the time the agent reads this.
const V1_SHARED_SECTIONS_NOTE =
  "Before this session started, these pages were compiled as one site: every v1 saved section, symbol and " +
  "section that several pages repeat (header, footer, FAQs, product details and the like) is one component " +
  "under app/_v1-shared/, taking what differs between pages as props (text, images, lists of items, optional " +
  "parts), and the pages render it with their own props; the v1 runtime is shared in app/_v1/. " +
  "app/_v1-shared/SHARED.md lists each component, the pages using it and why it was or was not merged " +
  "(no file means nothing was shared). Fix a shared component once, in app/_v1-shared/, and re-score the pages " +
  "that use it; keep a page's difference a prop rather than forking the component. Never copy a shared " +
  "component back into a page.";

// One page is worked straight through. A batch is worked from what is actually left: the site compile fills
// shared components' props from the live pages, which clears most of what each page's COMPILE-REPORT.md lists,
// and the judging round scores every page anyway, so walking the pages one by one first would check each twice.
function handoffWorkOrder({
  pageCount,
  cartItem,
}: {
  pageCount: number;
  cartItem: number | null;
}) {
  if (pageCount === 1) {
    return [
      "The compiler has already written and committed the route below and marked everything it could not finish with a data-v1-todo marker.",
      "Rebuild its bundle with the command shown (it includes --skip-compile, so the committed route is left exactly as it is; never recompile a route, or you throw away the work already in it), read its brief and its COMPILE-REPORT.md.",
      "Before you edit anything, call `v1-migration-check`: it has you run the site judge on the page as compiled (the baseline) and returns your work list, the sections that really differ from the live v1 page; the sections that already match or are close are done, so do not touch them.",
      `Then (1) wire the commerce markers — option and variant selects, quantity selectors, add-to-cart and every other cart action — against the product loader and cart hooks, (2) resolve every other data-v1-todo marker, (3) rebuild the sections on the work list${cartItem ? ", (4) rebuild the cart drawer (below)" : ""}, (${cartItem ? 5 : 4}) call \`v1-migration-check\` for the final judgement, which scores the page once more; what still differs becomes a plan in the Plans app for the merchant, listing what an agent can fix and what only they can do, so do not polish pixels now.`,
      "Commit as you go.",
    ].join(" ");
  }
  return [
    "The compiler has already written and committed every route below. Work the batch as one site, in this " +
      "order, and do not walk the pages one by one:",
    "0. Before you edit anything, call `v1-migration-check`. It has you run the site judge on the site as " +
      "compiled (the baseline) and returns your work list: the sections that really differ from their live " +
      "page, each with the live URL to compare against. Sections that already match or are close are done; do " +
      "not touch them.",
    "1. The markers that are actually left: `rg -n data-v1-todo app components` lists them. The counts below " +
      "come from each page's COMPILE-REPORT.md, written before the site compile, and most of them are gone. " +
      "Resolve what is left, commerce first (option and variant selects, quantity selectors, add-to-cart and " +
      "every other cart action, against the product loader and cart hooks), in app/_v1-shared/ before the pages. " +
      "Read a page's brief or report only for a marker in that page.",
    ...(cartItem
      ? [
          `2. The cart drawer (item ${cartItem} below): build it now, before the final judgement, since every page shows it.`,
        ]
      : []),
    `${cartItem ? 3 : 2}. The sections on your work list, each against its own page's live URL: in app/_v1-shared/ ` +
      "first when the section is shared. Never replace a section with another page's component.",
    `${cartItem ? 4 : 3}. Call \`v1-migration-check\` for the final judgement: the site judge pixel-scores ` +
      "every page once more and compares each section with the baseline. A section that got worse is rejected and " +
      "you restore its files (the check gives the command); if it is still worse on the next judgement, the check " +
      "restores them itself. What still differs becomes a plan in the Plans app, one todo per issue: what an " +
      "agent can fix, for the merchant to hand back to an agent later, and the steps only the merchant can take " +
      "(connecting apps, their needsUser notes). So do not polish pixels in this session. Rebuild a page's " +
      "bundle with the command shown only to read its brief (it includes --skip-compile, so the committed route " +
      "is left exactly as it is; never recompile a route, or you throw away the work already in it).",
    "Commit after each fix.",
  ].join("\n");
}

// The prompt Replo Admin's "Hand off to agent" starts its session with, once a batch has compiled every
// page. The compiler already wrote each route and left a `data-v1-todo` marker on everything it could not
// finish; this hands the whole batch to one session in the one sandbox, so no two writers ever touch the
// site at once. `v1-migration-check` reads the markers across every route, so it stays the only judge of
// when the batch is done.
export function buildV1MigrationHandoffPrompt({
  siteId,
  siteDirectory,
  pages,
  cart = null,
}: {
  siteId: string;
  siteDirectory: string;
  pages: V1MigrationHandoffPage[];
  /** The live store whose cart drawer the site's own drawer is rebuilt to match; null leaves the cart alone. */
  cart?: { storeUrl: string } | null;
}): string {
  const batch = pages.length > 1;
  const pageBlocks = pages.map((page, index) => {
    const previewPath = handoffPreviewPath(page);
    const extractFlags = [
      `--extract --element ${page.elementId} --route ${page.route} --site ${siteDirectory} --skip-compile`,
      ...(previewPath ? [`--preview-path ${previewPath}`] : []),
      ...(page.liveUrl ? [`--live-url ${page.liveUrl}`] : []),
    ];
    const extract = extractFlags.map((flags) => `\`${flags}\``).join(" ");
    // Counts by kind keep the prompt small however large the batch is; each route's COMPILE-REPORT.md has the
    // details.
    const kinds = new Map<string, number>();
    for (const todo of page.todos) {
      kinds.set(todo.kind, (kinds.get(todo.kind) ?? 0) + 1);
    }
    const todoSummary = [...kinds]
      .map(([kind, count]) => `${count} ${kind}`)
      .join(", ");
    const liveNote = page.liveUrl ? `, live v1 page ${page.liveUrl}` : "";
    return [
      `${index + 1}. ${HANDOFF_KIND_LABELS[page.kind]} "${page.name}" (element ${page.elementId}) at app${page.route}${liveNote}`,
      ...handoffKindNotes(page, previewPath),
      `   Bundle: ${extract}`,
      page.todos.length
        ? `   ${batch ? "Before the site compile, the compiler listed" : "What the compiler left for you"} (${page.todos.length}): ${todoSummary}. Every item, with its detail, is in app${page.route}/COMPILE-REPORT.md.`
        : "   The compiler left no markers.",
    ].join("\n");
  });
  const cartItem = cart ? pages.length + 1 : null;
  const cartBlock = cart
    ? [
        `${cartItem}. The cart drawer, site-wide (report route "cart"). The site's drawer is components/cart/slideout-cart.tsx; the live store's is ${cart.storeUrl}, usually a Shopify cart app loaded at runtime, so v1 has none of it and the compiler wrote nothing for it.`,
        "   Rebuild that drawer to look and work like the live one: open it on the live store (add a product, then look at the drawer) and match its header, promo banners, free-shipping or reward progress, line items (image, title, variant, quantity stepper, compare-at price and savings, a bundle's items), upsell list with its own add-to-cart, totals, checkout button and trust badges, in the theme's fonts and colors. Build it from the site's cart hooks and real product data (upsells are the store's real products, fetched like the product pages fetch theirs); never copy the cart app's markup, styles or scripts. Keep checkout, discounts and the cart's analytics working, and keep the drawer above every page.",
        `   The site judge scores it against the live drawer under route "cart". While you work on it, the skill's cart.mjs (\`node <skill>/scripts/cart.mjs --live ${cart.storeUrl} --site <site preview origin> --product <handle> --out <dir>\`) scores it on its own; check a bundle too if the store sells one.`,
      ].join("\n")
    : null;
  const judgedTargets = cart ? "every page and the cart drawer" : "every page";
  return [
    `Use the migrating-v1-pages skill to finish migrating ${pages.length} v1 page(s) into the site with siteId ${siteId}, whose working tree is the directory ${siteDirectory}. Read the skill first.`,
    handoffWorkOrder({ pageCount: pages.length, cartItem }),
    ...(batch ? [V1_SHARED_SECTIONS_NOTE] : []),
    [...pageBlocks, ...(cartBlock ? [cartBlock] : [])].join("\n\n"),
    [
      "Call the `v1-migration-check` tool first, before any edit, and again whenever you believe the markers and the work list are done, and do exactly what it returns.",
      `The site judge it has you run scores ${judgedTargets} against the live site and decides each section's status; you never grade a section yourself.`,
      "Its first judgement, of the site as compiled, is the baseline: work only on the sections it lists as differing, and never touch one it marks done.",
      "The final judgement is compared with the baseline, and a section you made worse is rejected until its files are restored.",
      "The check is done once every page was scored at the site's current state with nothing worse than the baseline; what still differs is written to a plan for the merchant, not fixed now.",
      'A section only a person can fix (an integration to connect, products missing from the store) records what they must do in its `needsUser`, and your last message ends with a "What you need to do" list of them.',
      "Your turn ends only when `v1-migration-check` says the migration is done or blocked.",
      "Never end a turn with a progress update or a note about what you will do next; keep working instead.",
    ].join(" "),
    "Do not ask which site or which pages; they are named above. Do not publish.",
  ].join("\n\n");
}

// (Noah, 2026-07-11, REPL-29203): Marks a main-chat session that a miniapp
// kicked off to generate a brand kit, so Branding can re-derive its
// "generating" state from session state (surviving a page refresh) instead of a
// local flag. Carried inside the main session's `meta` bag; also reused by the
// sendMessageToMainSession bridge input.
export const brandGenerationSessionMetadataSchema = z.object({
  brandSlug: z.string(),
});

/** One route of a v1 refine session, as the site judge rebuilds its reference and scores it. */
export const v1MigrationRefinePageSchema = z.object({
  route: z.string().startsWith("/"),
  elementId: z.uuid(),
  /** The live v1 page the reference is captured from; null when the store has no live URL for it. */
  liveUrl: z.url().nullable(),
  /** The URL path the site renders the route at: a product for a template, a placement for a theme section. */
  previewPath: z.string().startsWith("/"),
});

/**
 * The pages a v1 hand-off covers: on a refine session's metadata, and what a plans-only hand-off judges
 * without an agent.
 */
export const v1MigrationRefineMetaSchema = z.object({
  siteId: z.uuid(),
  route: z.string().startsWith("/"),
  routes: z.array(z.string().startsWith("/")).min(1).optional(),
  /** The session also rebuilds the site's cart drawer to match the live store's, judged as route "cart". */
  cart: z.boolean().optional(),
  /** What the site judge needs to score each route; absent on sessions started before it existed. */
  pages: z.array(v1MigrationRefinePageSchema).min(1).optional(),
  /** The live store whose cart drawer the site's is scored against, when `cart` is set. */
  cartStoreUrl: z.url().optional(),
});

// (Noah, 2026-07-11, REPL-29203): An open-ended bag of app-specific markers on a
// main session, kept separate from the session's identity so new markers can be
// added without touching the discriminated union.
export const mainSessionMetaSchema = z.object({
  brandGeneration: brandGenerationSessionMetadataSchema.optional(),
  roiReview: z.object({ reportFile: z.string().min(1) }).optional(),
  /**
   * The home goal this chat was started from. Goals have no server-verifiable
   * completion the way checklist steps do — nothing about "win back abandoned
   * carts" is a row we can query — so finishing the chat is the only signal we
   * get, and this is what tells us which card that finish belongs to.
   */
  startedGoalId: z.string().min(1).optional(),
  /**
   * A Replo Admin session finishing compiled v1 pages; see v1MigrationRefineStateSchema. `routes` is set
   * when one session takes a whole batch's leftover work, and then covers `route` too.
   */
  v1MigrationRefine: v1MigrationRefineMetaSchema.optional(),
  /** A Home workflow step this chat runs, and the page it builds; the run also stores the session id. */
  homeWorkflow: z
    .object({
      slug: z.string().min(1),
      stepSlug: z.string().min(1),
      siteId: z.uuid(),
      route: z.string().startsWith("/"),
    })
    .optional(),
});

/**
 * What the coordinator writes for the `v1-migration-check` tool when a refine session starts. The tool is
 * inert in any session without this file, which is what limits it to refine sessions.
 */
export const v1MigrationRefineSessionFileSchema = z
  .object({
    /** The site's working tree in the sandbox (e.g. `/workspace/dev/website`). */
    siteDirectory: z.string().min(1),
    /** Finds the port the site's dev server listens on, which the site judge loads the pages from. */
    siteId: z.uuid().optional(),
    route: z.string().startsWith("/"),
    /** Every route the session is finishing, when it took a whole batch; absent means only `route`. */
    routes: z.array(z.string().startsWith("/")).min(1).optional(),
    /** The judging report also covers the cart drawer, as sections with route "cart". */
    cart: z.boolean().optional(),
    /**
     * Every route's v1 element and the URLs the site judge (judge-site.mjs) scores it from. With it the
     * judge's scores decide each section's status; without it the agent's own report does.
     */
    pages: z.array(v1MigrationRefinePageSchema).min(1).optional(),
    cartStoreUrl: z.url().optional(),
    maxRounds: z.number().int().positive(),
  })
  .describe("V1MigrationRefineSessionFile");

/**
 * Where a refine session's loop is, kept by the `v1-migration-check` tool. `working` = markers left;
 * `judging` = markers gone, the agent owes its judging round and report; `done` / `blocked` / `capped`
 * end the loop. The coordinator's turn-end backstop re-prompts only while the status is not terminal,
 * and records each re-prompt in `nudges`.
 */
export const v1MigrationRefineStateSchema = z
  .object({
    status: z.enum(["working", "judging", "done", "blocked", "capped"]),
    round: z.number().int().nonnegative(),
    maxRounds: z.number().int().positive(),
    markersLeft: z.number().int().nonnegative(),
    markerFiles: z.array(
      z.object({ path: z.string(), count: z.number().int().nonnegative() }),
    ),
    nudges: z.number().int().nonnegative(),
    blocker: z.string().nullable(),
    /** The agent's judging round, copied from its report once the tool accepts it. */
    sections: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          status: z.enum(["match", "close", "off", "missing", "integration"]),
          notes: z.string(),
          /** What a person has to do before this section can match (connect an integration, add products). */
          needsUser: z.string().nullish(),
        }),
      )
      .nullable(),
    /**
     * One entry per site tree the site judge fully scored, with how many sections were off and the summed
     * worst mismatch, to compare hand-off runs.
     */
    judged: z
      .array(
        z.object({
          round: z.number().int().positive(),
          tree: z.string(),
          off: z.number().int().nonnegative(),
          worstSum: z.number().nonnegative(),
        }),
      )
      .optional(),
    /**
     * The site tree the site judge scored before the agent edited anything (baseline.json), which the final
     * judgement is compared against. Null while that judgement is owed; absent on sessions without one.
     */
    baselineTree: z.string().nullish(),
    /** Each final judgement rejected because a section scored worse than the baseline. */
    regressions: z
      .array(
        z.object({
          round: z.number().int().positive(),
          tree: z.string(),
          sections: z.number().int().positive(),
        }),
      )
      .optional(),
    updatedAt: z.string(),
  })
  .describe("V1MigrationRefineState");

export const V1_MIGRATION_REFINE_TERMINAL_STATUSES = [
  "done",
  "blocked",
  "capped",
] as const;

export const sessionMetadataSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("main"),
      meta: mainSessionMetaSchema.optional(),
    }),
    z.object({
      type: z.literal("subagent"),
      parentToolCallId: z.string().nullable(),
    }),
    z.object({ type: z.literal("filesystemSession") }),
    z.object({ type: z.literal("scheduledTask") }),
    z.object({
      type: z.literal("siteReport"),
      siteId: z.uuid(),
      reportType: siteReportTypeSchema,
      reportId: z.uuid(),
    }),
    ...sideSessionMetadataOptions,
  ])
  .describe("SessionMetadata");

// A composer message the user queued while the agent was busy. `parts` are the
// already-resolved wire parts (durable file urls/ids), i.e. exactly what a send
// would transmit — so a persisted queued message is self-contained and needs no
// attachment reconstruction on restore.
export const persistedQueuedMessageSchema = z
  .object({
    id: z.string(),
    prompt: z.string(),
    parts: uiMessagePartsSchema,
  })
  .describe("PersistedQueuedMessage");
